import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "../utils/childrensBookAssetState";
import {
  findOversizedManuscriptUnits,
} from "../utils/childrensBookManuscriptCapacity";
import {
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";

import {
  buildChildrensBookUnitDiagnostics,
  joinChildrensBookDiagnosticMessages,
} from "../utils/childrensBookUnitDiagnostics";

function textPresent(value) {
  return Boolean(
    String(value ?? "").trim()
  );
}

function decisionMade(value) {
  const normalized =
    String(value ?? "").trim();

  return (
    textPresent(normalized) &&
    normalized !== "not_decided"
  );
}

function trimSizeComplete(
  trimSize,
  customTrimSize
) {
  const normalized =
    String(trimSize ?? "").trim();

  if (!decisionMade(normalized)) {
    return false;
  }

  if (
    normalized ===
    "custom_dimensions"
  ) {
    return textPresent(
      customTrimSize
    );
  }

  return true;
}

function statusStyle(status) {
  const base = {
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  };

  if (status === "ready") {
    return { ...base, background: "#dcfce7", color: "#166534" };
  }

  if (status === "warning") {
    return { ...base, background: "#fef3c7", color: "#92400e" };
  }

  return { ...base, background: "#fee2e2", color: "#b91c1c" };
}

export default function ChildrensBookPublicationReadiness({
  lang = "en",
  projectPlan,
  projectId = "",
  childrensBookAssets = [],
  onLoadPublicationCompatibility,
  onLoadPublicationProduction,
  onLoadPublicationInteriorReview,
  onLoadPublicationCoverReview,
  onLoadPublicationFinalInterior,
  onLoadPublicationFinalCover,
  onRegeneratePublicationInterior,
  onApprovePublicationInterior,
  onRegeneratePublicationCover,
  onApprovePublicationCover,
  onStartPublicationProduction,
  onOpenBookAssembly,
  onOpenPublicationProof,
  onOpenProjectPlan,
  onOpenBookMap,
  onOpenCoverPlan,
  onOpenManuscriptPlan,
  onOpenIllustrationPlan,
  onReturn,
  onExit,
}) {
  const isFR = String(lang).toLowerCase() === "fr";

  const units = useMemo(
    () =>
      Array.isArray(projectPlan.bookMap?.units)
        ? projectPlan.bookMap.units
        : [],
    [projectPlan.bookMap?.units]
  );

  const manuscriptEntries = useMemo(
    () =>
      Array.isArray(projectPlan.manuscriptPlan?.entries)
        ? projectPlan.manuscriptPlan.entries
        : [],
    [projectPlan.manuscriptPlan?.entries]
  );

  const oversizedManuscriptUnits =
    useMemo(
      () =>
        findOversizedManuscriptUnits(
          projectPlan
        ),
      [projectPlan]
    );

  const manuscriptLayoutReady =
    oversizedManuscriptUnits
      .length === 0;

  const illustrationEntries = useMemo(
    () =>
      Array.isArray(projectPlan.illustrationPlan?.entries)
        ? projectPlan.illustrationPlan.entries
        : [],
    [projectPlan.illustrationPlan?.entries]
  );

  const estimatedPages = units.reduce(
    (total, unit) =>
      total +
      (unit.unitType === "double_page_spread" ? 2 : 1),
    0
  );

  const projectReady =
    textPresent(
      projectPlan.identity?.title
    ) &&
    textPresent(
      projectPlan.audience?.ageRange
    ) &&
    decisionMade(
      projectPlan.audience
        ?.readingLevel
    ) &&
    textPresent(
      projectPlan.bookForm?.type
    ) &&
    trimSizeComplete(
      projectPlan.visualFormat
        ?.trimSize,
      projectPlan.visualFormat
        ?.customTrimSize
    ) &&
    textPresent(
      projectPlan.collaboration?.mode
    );

  const bookMapReady =
    units.length > 0;

  const coverRequired = [
    projectPlan.coverPlan?.title,
    projectPlan.coverPlan?.authorName,
  ];
  const coverReady = coverRequired.every(textPresent);

  const completedManuscriptIds = new Set(
    manuscriptEntries
      .filter(
        (entry) =>
          textPresent(
            plainTextFromManuscriptEntry(
              entry
            )
          ) &&
          entry.status === "complete"
      )
      .map((entry) => entry.unitId)
  );

  /*
   * "No text" is an explicit Book Structure decision.
   *
   * Such a unit is complete for manuscript-requirement purposes
   * without artificial placeholder prose. Illustration and other
   * requirements remain independently enforced.
   */
  const manuscriptSatisfiedIds =
    new Set(
      completedManuscriptIds
    );

  units.forEach(
    (unit) => {
      if (
        String(
          unit?.textAmount || ""
        )
          .trim()
          .toLowerCase() ===
        "none"
      ) {
        const unitId =
          String(
            unit?.id || ""
          ).trim();

        if (unitId) {
          manuscriptSatisfiedIds.add(
            unitId
          );
        }
      }
    }
  );

  const manuscriptReady =
    units.length > 0 &&
    manuscriptLayoutReady &&
    units.every((unit) =>
      manuscriptSatisfiedIds.has(
        unit.id
      )
    );

  const illustrationUnits = units.filter(
    (unit) =>
      unit.illustrationPlan && unit.illustrationPlan !== "none"
  );

  const completedIllustrationIds = new Set(
    illustrationEntries
      .filter(
        (entry) =>
          entry.status === "complete"
      )
      .map(
        (entry) =>
          entry.unitId
      )
  );

  const illustrationReady =
    bookMapReady &&
    (
      illustrationUnits.length === 0 ||
      illustrationUnits.every((unit) =>
        completedIllustrationIds.has(unit.id)
      )
    );

  const activeCover =
    currentChildrensBookCover(
      childrensBookAssets,
      "front_cover"
    );

  const illustrationAssetsByUnit =
    childrensBookIllustrationsByUnit(
      childrensBookAssets
    );

  const oversizedUnitIds =
    useMemo(
      () =>
        oversizedManuscriptUnits
          .map(
            (item) =>
              String(
                item?.capacity
                  ?.unitId ||
                item?.unit?.id ||
                ""
              ).trim()
          )
          .filter(Boolean),
      [
        oversizedManuscriptUnits,
      ]
    );

  const unitDiagnostics =
    useMemo(
      () =>
        buildChildrensBookUnitDiagnostics(
          projectPlan,
          childrensBookAssets,
          {
            lang: isFR
              ? "fr"
              : "en",
            oversizedUnitIds,
          }
        ),
      [
        projectPlan,
        childrensBookAssets,
        isFR,
        oversizedUnitIds,
      ]
    );

  const manuscriptDiagnosticText =
    joinChildrensBookDiagnosticMessages(
      unitDiagnostics.manuscript
    );

  const illustrationDiagnosticText =
    joinChildrensBookDiagnosticMessages(
      unitDiagnostics
        .illustrationPlan
    );

  const assemblyDiagnosticText =
    joinChildrensBookDiagnosticMessages(
      [
        ...unitDiagnostics.manuscript,
        ...unitDiagnostics.cover,
        ...unitDiagnostics.assembly.filter(
          (item) =>
            item.kind ===
            "missing_illustration_asset"
        ),
      ]
    );

  const assemblyReady =
    manuscriptReady &&
    Boolean(activeCover) &&
    illustrationUnits.every(
      (unit) =>
        Boolean(
          illustrationAssetsByUnit[
            unit.id
          ]
        )
    );

  const titleComparable =
    textPresent(projectPlan.coverPlan?.title) &&
    textPresent(projectPlan.identity?.title);

  const titleMatches =
    titleComparable &&
    String(projectPlan.coverPlan.title).trim() ===
      String(projectPlan.identity.title).trim();

  const authorIdentity =
    projectPlan.identity?.penName ||
    projectPlan.identity?.authorName;

  const authorComparable =
    textPresent(projectPlan.coverPlan?.authorName) &&
    textPresent(authorIdentity);

  const authorMatches =
    authorComparable &&
    String(projectPlan.coverPlan.authorName).trim() ===
      String(authorIdentity).trim();

  const checks = [
    {
      id: "project",
      title: isFR ? "Plan du projet" : "Project Plan",
      description: isFR
        ? "Titre, public, forme, dimensions et méthode de travail."
        : "Title, audience, form, dimensions, and working method.",
      ready: projectReady,
      detail: projectReady
        ? isFR
          ? "Les informations essentielles du projet sont présentes."
          : "The essential project information is present."
        : isFR
          ? "Complétez les informations générales du projet."
          : "Complete the general project information.",
      onOpen: onOpenProjectPlan,
      actionLabel: isFR
        ? "Ouvrir le plan du projet"
        : "Open Project Plan",
    },
    {
      id: "book-map",
      title: isFR ? "Structure du livre" : "Book Structure",
      description: isFR
        ? `${units.length} unité(s) · environ ${estimatedPages} page(s).`
        : `${units.length} unit(s) · about ${estimatedPages} page(s).`,
      ready: bookMapReady,
      detail: bookMapReady
        ? isFR
          ? "Toutes les unités ont un objectif et un état planifié."
          : "Every unit has a purpose and a planned status."
        : isFR
          ? "Ajoutez des unités ou complétez leur objectif et leur état."
          : "Add units or complete their purpose and status.",
      onOpen: onOpenBookMap,
      actionLabel: isFR
        ? "Ouvrir la structure du livre"
        : "Open Book Structure",
    },
    {
      id: "cover",
      title: isFR ? "Plan de couverture" : "Cover Plan",
      description: isFR
        ? "Titre affiché, nom d’auteur, concept visuel et ambiance."
        : "Displayed title, author name, visual concept, and mood.",
      ready: coverReady,
      detail: coverReady
        ? isFR
          ? "Les éléments essentiels de la couverture sont présents."
          : "The essential cover elements are present."
        : isFR
          ? "Complétez les éléments essentiels de la couverture."
          : "Complete the essential cover elements.",
      onOpen: onOpenCoverPlan,
      actionLabel: isFR
        ? "Ouvrir le plan de couverture"
        : "Open Cover Plan",
    },
    {
      id: "manuscript",
      title: isFR ? "Plan du manuscrit" : "Manuscript Plan",
      description: isFR
        ? `${manuscriptSatisfiedIds.size} sur ${units.length} unité(s) terminée(s).`
        : `${manuscriptSatisfiedIds.size} of ${units.length} unit(s) complete.`,
      ready: manuscriptReady,
      detail:
        manuscriptDiagnosticText
          ? manuscriptDiagnosticText
          : manuscriptReady
            ? (
                isFR
                  ? "Chaque unité du livre possède le contenu manuscrit requis par sa Structure du livre."
                  : "Every book unit has the manuscript content required by its Book Structure."
              )
            : (
                isFR
                  ? "Complétez le manuscrit uniquement pour les unités qui nécessitent du texte."
                  : "Complete manuscript content only for units that require text."
              ),
      onOpen: onOpenManuscriptPlan,
      actionLabel: isFR
        ? "Ouvrir le plan du manuscrit"
        : "Open Manuscript Plan",
    },
    {
      id: "illustrations",
      title: isFR ? "Plan des illustrations" : "Illustration Plan",
      description: isFR
        ? `${completedIllustrationIds.size} sur ${illustrationUnits.length} illustration(s) terminée(s).`
        : `${completedIllustrationIds.size} of ${illustrationUnits.length} illustration(s) complete.`,
      ready: illustrationReady,
      detail:
        illustrationDiagnosticText
          ? illustrationDiagnosticText
          : illustrationReady
            ? illustrationUnits.length === 0
              ? isFR
                ? "Aucune illustration n’est requise par le Plan du livre."
                : "The Book Structure requires no illustrations."
              : isFR
                ? "Toutes les illustrations requises ont une directive terminée."
                : "Every required illustration has a completed direction."
            : isFR
              ? "Complétez les directives des illustrations requises."
              : "Complete the directions for all required illustrations.",
      onOpen: onOpenIllustrationPlan,
      actionLabel: isFR
        ? "Ouvrir le plan des illustrations"
        : "Open Illustration Plan",
    },
    {
      id: "book-assembly",
      title: isFR
        ? "Assemblage du livre"
        : "Book Assembly",
      description: isFR
        ? "Couverture active, manuscrit terminé et illustrations requises jointes."
        : "Active cover, completed manuscript, and required illustration files.",
      ready: assemblyReady,
      detail:
        assemblyReady
          ? (
              isFR
                ? "Les ressources nécessaires sont réunies pour examiner le livre assemblé."
                : "The required assets are present for assembled-book review."
            )
          : (
              assemblyDiagnosticText ||
              (
                isFR
                  ? "Vérifiez les ressources manquantes du livre assemblé."
                  : "Review the missing assembled-book assets."
              )
            ),
      onOpen: onOpenBookAssembly,
      actionLabel: isFR
        ? "Ouvrir l’assemblage du livre"
        : "Open Book Assembly",
    },
  ];

  const warnings = [
    {
      id: "title-match",
      ready: titleMatches,
      pending: !titleComparable,
      title: isFR ? "Cohérence du titre" : "Title consistency",
      detail: !titleComparable
        ? isFR
          ? "Ajoutez d’abord le titre du projet et le titre affiché sur la couverture."
          : "Add both the project title and displayed cover title before comparing them."
        : titleMatches
          ? isFR
            ? "Le titre du projet et celui de la couverture sont cohérents."
            : "The project title and cover title are consistent."
          : isFR
            ? "Le titre de la couverture ne correspond pas au titre du projet."
            : "The cover title does not match the project title.",
      onOpen: onOpenCoverPlan,
    },
    {
      id: "author-match",
      ready: authorMatches,
      pending: !authorComparable,
      title: isFR
        ? "Cohérence du nom d’auteur"
        : "Author-name consistency",
      detail: !authorComparable
        ? isFR
          ? "Ajoutez d’abord le nom d’auteur public dans le projet et sur la couverture."
          : "Add the public author name in both the project and Cover Plan before comparing them."
        : authorMatches
          ? isFR
            ? "Le nom d’auteur public est cohérent."
            : "The public author name is consistent."
          : isFR
            ? "Le nom d’auteur de la couverture ne correspond pas au nom choisi dans le projet."
            : "The cover author name does not match the name chosen in the project.",
      onOpen: onOpenCoverPlan,
    },
  ];

  const blockerCount = checks.filter((check) => !check.ready).length;
  const warningCount = warnings.filter(
    (warning) =>
      !warning.ready &&
      !warning.pending
  ).length;
  const readyCount = checks.length - blockerCount;

  const overallStatus =
    blockerCount === 0 && warningCount === 0
      ? "ready"
      : blockerCount === 0
        ? "warning"
        : "blocked";

  const overallText = {
    ready:
      isFR
        ? "Le contenu de votre livre est prêt pour la mise en page finale"
        : "Your book content is ready for final layout",

    warning:
      isFR
        ? (
            warningCount === 1
              ? "1 détail à vérifier"
              : `${warningCount} détails à vérifier`
          )
        : (
            warningCount === 1
              ? "1 detail to review"
              : `${warningCount} details to review`
          ),

    blocked:
      isFR
        ? (
            blockerCount === 1
              ? "1 élément nécessite votre attention"
              : `${blockerCount} éléments nécessitent votre attention`
          )
        : (
            blockerCount === 1
              ? "1 thing needs attention"
              : `${blockerCount} things need attention`
          ),
  }[overallStatus];

  const [
    detailsOpen,
    setDetailsOpen,
  ] = useState(false);

  const [
    publicationPreparation,
    setPublicationPreparation,
  ] = useState(null);

  const [
    publicationPreparationLoading,
    setPublicationPreparationLoading,
  ] = useState(false);

  const [
    publicationPreparationError,
    setPublicationPreparationError,
  ] = useState("");

  const [
    publicationPreparationStarting,
    setPublicationPreparationStarting,
  ] = useState(false);

  const [
    publicationInteriorOpening,
    setPublicationInteriorOpening,
  ] = useState(false);

  const [
    publicationInteriorError,
    setPublicationInteriorError,
  ] = useState("");

  const [
    publicationInteriorRegenerating,
    setPublicationInteriorRegenerating,
  ] = useState(false);

  const [
    publicationInteriorRegenerationError,
    setPublicationInteriorRegenerationError,
  ] = useState("");

  const [
    publicationCoverOpening,
    setPublicationCoverOpening,
  ] = useState(false);

  const [
    publicationCoverError,
    setPublicationCoverError,
  ] = useState("");

  const [
    publicationCoverRegenerating,
    setPublicationCoverRegenerating,
  ] = useState(false);

  const [
    publicationCoverRegenerationError,
    setPublicationCoverRegenerationError,
  ] = useState("");

  const [
    publicationFinalInteriorOpening,
    setPublicationFinalInteriorOpening,
  ] = useState(false);

  const [
    publicationFinalInteriorError,
    setPublicationFinalInteriorError,
  ] = useState("");

  const [
    publicationFinalCoverOpening,
    setPublicationFinalCoverOpening,
  ] = useState(false);

  const [
    publicationFinalCoverError,
    setPublicationFinalCoverError,
  ] = useState("");

  const [
    publicationInteriorApproving,
    setPublicationInteriorApproving,
  ] = useState(false);

  const [
    publicationInteriorApprovalError,
    setPublicationInteriorApprovalError,
  ] = useState("");

  const [
    publicationCoverApproving,
    setPublicationCoverApproving,
  ] = useState(false);

  const [
    publicationCoverApprovalError,
    setPublicationCoverApprovalError,
  ] = useState("");

  const [
    paperType,
    setPaperType,
  ] = useState("");

  const [
    interiorColor,
    setInteriorColor,
  ] = useState("");

  const [
    publicationCompatibilityPreview,
    setPublicationCompatibilityPreview,
  ] = useState(null);

  const [
    publicationCompatibilityPreviewLoading,
    setPublicationCompatibilityPreviewLoading,
  ] = useState(false);

  const [
    publicationCompatibilityPreviewError,
    setPublicationCompatibilityPreviewError,
  ] = useState("");

  const productionProjectId =
    String(
      projectId || ""
    ).trim();

  const publicationFormat =
    String(
      projectPlan.visualFormat
        ?.publicationFormat || ""
    )
      .trim()
      .toLowerCase();

  const printPublicationSelected =
    publicationFormat === "print" ||
    publicationFormat ===
      "print_and_digital";

  const productionTrim =
    String(
      projectPlan.visualFormat
        ?.trimSize || ""
    )
      .trim()
      .toLowerCase();

  const productionBleed =
    String(
      projectPlan.visualFormat
        ?.bleed || ""
    )
      .trim()
      .toLowerCase();

  const productionOrientation =
    String(
      projectPlan.visualFormat
        ?.orientation || ""
    )
      .trim()
      .toLowerCase();

  const productionTrimOrientationSupported =
    (
      productionTrim ===
        "6_x_9_in" &&
      (
        !productionOrientation ||
        productionOrientation ===
          "portrait"
      )
    ) ||
    (
      productionTrim ===
        "8.25_x_8.25_in" &&
      productionOrientation ===
        "square"
    ) ||
    (
      productionTrim ===
        "8.5_x_8.5_in" &&
      productionOrientation ===
        "square"
    ) ||
    (
      productionTrim ===
        "5.5_x_8.5_in" &&
      productionOrientation ===
        "portrait"
    ) ||
    (
      productionTrim ===
        "8_x_10_in" &&
      productionOrientation ===
        "portrait"
    ) ||
    (
      productionTrim ===
        "8.5_x_11_in" &&
      productionOrientation ===
        "portrait"
    );

  const printProductionSetupSupported =
    printPublicationSelected &&
    productionTrimOrientationSupported &&
    (
      productionBleed === "yes" ||
      productionBleed === "no"
    );

  useEffect(() => {
    if (
      overallStatus !== "ready" ||
      !printProductionSetupSupported ||
      !productionProjectId ||
      typeof
        onLoadPublicationProduction !==
        "function"
    ) {
      return undefined;
    }

    let cancelled = false;

    async function loadPublicationPreparation() {
      try {
        setPublicationPreparationLoading(
          true
        );

        setPublicationPreparationError(
          ""
        );

        const result =
          await onLoadPublicationProduction(
            productionProjectId
          );

        if (cancelled) return;

        const preparation =
          result?.publicationPreparation;

        if (
          !preparation ||
          typeof preparation !==
            "object" ||
          Array.isArray(
            preparation
          )
        ) {
          throw new Error(
            "Publication preparation state is unavailable."
          );
        }

        setPublicationPreparation(
          preparation
        );

        if (
          preparation.started === true
        ) {
          setPaperType(
            String(
              preparation
                .printSettings
                ?.paperType || ""
            )
              .trim()
              .toLowerCase()
          );

          setInteriorColor(
            String(
              preparation
                .printSettings
                ?.interiorColor || ""
            )
              .trim()
              .toLowerCase()
          );
        }
      } catch (error) {
        if (cancelled) return;

        setPublicationPreparationError(
          isFR
            ? "Impossible de charger l’état de préparation pour publication."
            : "Unable to load publication preparation."
        );
      } finally {
        if (!cancelled) {
          setPublicationPreparationLoading(
            false
          );
        }
      }
    }

    loadPublicationPreparation();

    return () => {
      cancelled = true;
    };
  }, [
    isFR,
    onLoadPublicationProduction,
    overallStatus,
    printProductionSetupSupported,
    productionProjectId,
  ]);

  const publicationAlreadyStarted =
    publicationPreparation
      ?.started === true;

  const colourPaperConflict =
    paperType === "cream" &&
    (
      interiorColor ===
        "standard_color" ||
      interiorColor ===
        "premium_color"
    );

  const publicationChoicesComplete =
    Boolean(
      paperType &&
      interiorColor &&
      !colourPaperConflict
    );

  useEffect(() => {
    if (
      publicationAlreadyStarted ||
      !publicationChoicesComplete ||
      !printProductionSetupSupported ||
      !productionProjectId ||
      typeof
        onLoadPublicationCompatibility !==
        "function"
    ) {
      setPublicationCompatibilityPreview(
        null
      );

      setPublicationCompatibilityPreviewError(
        ""
      );

      setPublicationCompatibilityPreviewLoading(
        false
      );

      return undefined;
    }

    let cancelled = false;

    async function loadCompatibilityPreview() {
      try {
        setPublicationCompatibilityPreviewLoading(
          true
        );

        setPublicationCompatibilityPreviewError(
          ""
        );

        const result =
          await onLoadPublicationCompatibility(
            productionProjectId,
            {
              paperType,
              interiorColor,
            }
          );

        if (cancelled) return;

        const compatibility =
          result
            ?.publicationCompatibility;

        if (
          !compatibility ||
          typeof compatibility !==
            "object" ||
          Array.isArray(
            compatibility
          )
        ) {
          throw new Error(
            "Print compatibility is unavailable."
          );
        }

        setPublicationCompatibilityPreview(
          compatibility
        );
      } catch {
        if (cancelled) return;

        setPublicationCompatibilityPreview(
          null
        );

        setPublicationCompatibilityPreviewError(
          isFR
            ? "Impossible de vérifier les limites de pages de cette option d’impression pour le moment."
            : "Unable to check the page limits for this print option right now."
        );
      } finally {
        if (!cancelled) {
          setPublicationCompatibilityPreviewLoading(
            false
          );
        }
      }
    }

    loadCompatibilityPreview();

    return () => {
      cancelled = true;
    };
  }, [
    interiorColor,
    isFR,
    onLoadPublicationCompatibility,
    paperType,
    printProductionSetupSupported,
    publicationAlreadyStarted,
    productionProjectId,
    publicationChoicesComplete,
  ]);

  const compatibilityPreviewMinimum =
    Number(
      publicationCompatibilityPreview
        ?.minimumPageCount
    );

  const compatibilityPreviewMaximum =
    Number(
      publicationCompatibilityPreview
        ?.maximumPageCount
    );

  const compatibilityPreviewRangeKnown =
    Number.isInteger(
      compatibilityPreviewMinimum
    ) &&
    compatibilityPreviewMinimum > 0 &&
    Number.isInteger(
      compatibilityPreviewMaximum
    ) &&
    compatibilityPreviewMaximum >=
      compatibilityPreviewMinimum;

  const plannedBookStructurePageCount =
    units.reduce(
      (total, unit) =>
        total +
        (
          String(
            unit?.unitType || ""
          )
            .trim()
            .toLowerCase() ===
            "double_page_spread"
              ? 2
              : 1
        ),
      0
    );

  const plannedStructureBelowMinimum =
    compatibilityPreviewRangeKnown &&
    plannedBookStructurePageCount > 0 &&
    plannedBookStructurePageCount <
      compatibilityPreviewMinimum;

  const productionTrimDisplay =
    {
      "5.5_x_8.5_in":
        "5.5 × 8.5",
      "6_x_9_in":
        "6 × 9",
      "8.25_x_8.25_in":
        "8.25 × 8.25",
      "8.5_x_8.5_in":
        "8.5 × 8.5",
      "8_x_10_in":
        "8 × 10",
      "8.5_x_11_in":
        "8.5 × 11",
    }[productionTrim] ||
    productionTrim;

  const publicationStartAvailable =
    typeof
      onStartPublicationProduction ===
      "function";

  const canStartPublicationPreparation =
    publicationStartAvailable &&
    publicationChoicesComplete &&
    !publicationAlreadyStarted &&
    !publicationPreparationLoading &&
    !publicationPreparationStarting;

  async function startPublicationPreparation() {
    if (
      !canStartPublicationPreparation &&
      !canRetryPublicationPreparation
    ) {
      return;
    }

    try {
      setPublicationPreparationStarting(
        true
      );

      setPublicationPreparationError(
        ""
      );

      const result =
        await onStartPublicationProduction(
          productionProjectId,
          {
            paperType,
            interiorColor,
          }
        );

      const preparation =
        result?.publicationPreparation;

      if (
        !preparation ||
        typeof preparation !==
          "object" ||
        Array.isArray(
          preparation
        )
      ) {
        throw new Error(
          "Publication preparation state is unavailable."
        );
      }

      setPublicationPreparation(
        preparation
      );

      setPaperType(
        String(
          preparation
            .printSettings
            ?.paperType ||
          paperType
        )
          .trim()
          .toLowerCase()
      );

      setInteriorColor(
        String(
          preparation
            .printSettings
            ?.interiorColor ||
          interiorColor
        )
          .trim()
          .toLowerCase()
      );
    } catch (error) {
      setPublicationPreparationError(
        String(
          error?.message || ""
        ).trim() ||
        (
          isFR
            ? "Impossible de démarrer la préparation pour publication."
            : "Unable to start publication preparation."
        )
      );
    } finally {
      setPublicationPreparationStarting(
        false
      );
    }
  }

  const productionWriterState =
    String(
      publicationPreparation
        ?.writerState || ""
    )
      .trim()
      .toLowerCase();

  const pageCountCompatibility =
    publicationPreparation
      ?.pageCountCompatibility &&
    typeof publicationPreparation
      .pageCountCompatibility ===
      "object" &&
    !Array.isArray(
      publicationPreparation
        .pageCountCompatibility
    )
      ? publicationPreparation
          .pageCountCompatibility
      : null;

  const currentPublicationPageCount =
    Number(
      pageCountCompatibility
        ?.currentPageCount
    );

  const minimumPublicationPageCount =
    Number(
      pageCountCompatibility
        ?.minimumPageCount
    );

  const maximumPublicationPageCount =
    Number(
      pageCountCompatibility
        ?.maximumPageCount
    );

  const publicationPageCountKnown =
    Number.isInteger(
      currentPublicationPageCount
    ) &&
    currentPublicationPageCount > 0;

  const publicationPageRangeKnown =
    Number.isInteger(
      minimumPublicationPageCount
    ) &&
    minimumPublicationPageCount > 0 &&
    Number.isInteger(
      maximumPublicationPageCount
    ) &&
    maximumPublicationPageCount >=
      minimumPublicationPageCount;

  const publicationPageCountBelowMinimum =
    publicationPageCountKnown &&
    publicationPageRangeKnown &&
    currentPublicationPageCount <
      minimumPublicationPageCount;

  const publicationPageCountAboveMaximum =
    publicationPageCountKnown &&
    publicationPageRangeKnown &&
    currentPublicationPageCount >
      maximumPublicationPageCount;

  const publicationPhysicalPairIncomplete =
    publicationPageCountKnown &&
    pageCountCompatibility
      ?.evenPageCount === false;

  const publicationInteriorApprovalPageReady =
    !pageCountCompatibility ||
    pageCountCompatibility
      .readyForInteriorApproval !==
      false;

  let publicationInteriorCompatibilityMessage =
    "";

  if (
    publicationPageCountBelowMinimum
  ) {
    publicationInteriorCompatibilityMessage =
      isFR
        ? `Votre intérieur finalisé comporte ${currentPublicationPageCount} pages. Le format d’impression sélectionné exige au moins ${minimumPublicationPageCount} pages. Votre livre reste valable, mais ce parcours d’impression ne peut pas continuer tant que l’intérieur finalisé n’atteint pas cette limite.`
        : `Your finished interior has ${currentPublicationPageCount} pages. The selected print format requires at least ${minimumPublicationPageCount} pages. Your book is still valid, but this print-production path cannot continue until the finished interior reaches that minimum.`;
  } else if (
    publicationPageCountAboveMaximum
  ) {
    publicationInteriorCompatibilityMessage =
      isFR
        ? `Votre intérieur finalisé comporte ${currentPublicationPageCount} pages. Le format d’impression sélectionné accepte au maximum ${maximumPublicationPageCount} pages. Votre livre reste valable, mais ce parcours d’impression ne peut pas continuer avec ce nombre de pages.`
        : `Your finished interior has ${currentPublicationPageCount} pages. The selected print format supports at most ${maximumPublicationPageCount} pages. Your book is still valid, but this print-production path cannot continue with this page count.`;
  } else if (
    publicationPhysicalPairIncomplete
  ) {
    publicationInteriorCompatibilityMessage =
      isFR
        ? "NextScenes doit terminer la dernière paire de pages physiques avant que l’intérieur puisse être approuvé. Vous n’avez pas besoin de modifier votre contenu créatif pour cette raison."
        : "NextScenes must complete the final physical page pair before the interior can be approved. You do not need to change your creative content for this reason.";
  } else if (
    pageCountCompatibility &&
    pageCountCompatibility
      .readyForInteriorApproval === false
  ) {
    publicationInteriorCompatibilityMessage =
      isFR
        ? "NextScenes ne peut pas encore confirmer que cet intérieur respecte les limites du format d’impression sélectionné. L’approbation est suspendue jusqu’à ce que cette incompatibilité soit résolue."
        : "NextScenes cannot yet confirm that this interior fits the selected print format. Approval is paused until the compatibility issue is resolved.";
  }

  const canRetryPublicationPreparation =
    publicationStartAvailable &&
    publicationChoicesComplete &&
    publicationAlreadyStarted &&
    productionWriterState !==
      "review_interior" &&
    productionWriterState !==
      "review_cover" &&
    productionWriterState !==
      "ready_for_publication" &&
    !publicationPreparationLoading &&
    !publicationPreparationStarting;

  const productionWriterStateText =
    {
      preparing:
        isFR
          ? "Préparation de votre livre…"
          : "Preparing your book…",

      review_interior:
        isFR
          ? "Vérifiez votre livre finalisé"
          : "Review your finished book",

      review_cover:
        isFR
          ? "Vérifiez votre couverture finalisée"
          : "Review your finished cover",

      ready_for_publication:
        isFR
          ? "Prêt pour publication"
          : "Ready for Publication",

      needs_attention:
        isFR
          ? "La préparation pour publication nécessite votre attention."
          : "Publication preparation needs attention.",
    }[
      productionWriterState
    ] ||
    (
      isFR
        ? "Préparation pour publication démarrée."
        : "Publication preparation started."
    );

  const canRegeneratePublicationInterior =
    productionWriterState ===
      "review_interior" &&
    typeof
      onRegeneratePublicationInterior ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    !publicationInteriorOpening &&
    !publicationInteriorRegenerating &&
    !publicationInteriorApproving;

  async function regeneratePublicationInterior() {
    if (
      !canRegeneratePublicationInterior
    ) {
      return;
    }

    try {
      setPublicationInteriorRegenerating(
        true
      );

      setPublicationInteriorRegenerationError(
        ""
      );

      /*
       * Keep the three Interior actions visibly and operationally
       * separate. Regeneration is neither viewing nor approval.
       */
      setPublicationInteriorError(
        ""
      );

      setPublicationInteriorApprovalError(
        ""
      );

      const result =
        await onRegeneratePublicationInterior(
          productionProjectId
        );

      const preparation =
        result?.publicationPreparation;

      if (
        !preparation ||
        typeof preparation !==
          "object" ||
        Array.isArray(
          preparation
        )
      ) {
        throw new Error(
          "Publication preparation state is unavailable."
        );
      }

      /*
       * The backend leaves the book at Creator Interior Review.
       * Adopt only its writer-safe persisted state.
       */
      setPublicationPreparation(
        preparation
      );
    } catch {
      setPublicationInteriorRegenerationError(
        isFR
          ? "Impossible de régénérer l’intérieur finalisé pour le moment."
          : "Unable to regenerate the finished interior right now."
      );
    } finally {
      setPublicationInteriorRegenerating(
        false
      );
    }
  }

  const canApprovePublicationInterior =
    productionWriterState ===
      "review_interior" &&
    typeof
      onApprovePublicationInterior ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    publicationInteriorApprovalPageReady &&
    !publicationInteriorOpening &&
    !publicationInteriorRegenerating &&
    !publicationInteriorApproving;

  async function approvePublicationInterior() {
    if (
      !canApprovePublicationInterior
    ) {
      return;
    }

    try {
      setPublicationInteriorApproving(
        true
      );

      setPublicationInteriorApprovalError(
        ""
      );

      const result =
        await onApprovePublicationInterior(
          productionProjectId
        );

      const preparation =
        result?.publicationPreparation;

      if (
        !preparation ||
        typeof preparation !==
          "object" ||
        Array.isArray(
          preparation
        )
      ) {
        throw new Error(
          "Publication preparation state is unavailable."
        );
      }

      setPublicationPreparation(
        preparation
      );
    } catch {
      setPublicationInteriorApprovalError(
        isFR
          ? "Impossible d’approuver l’intérieur finalisé pour le moment."
          : "Unable to approve the finished interior right now."
      );
    } finally {
      setPublicationInteriorApproving(
        false
      );
    }
  }

  const canRegeneratePublicationCover =
    productionWriterState ===
      "review_cover" &&
    typeof
      onRegeneratePublicationCover ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    !publicationCoverOpening &&
    !publicationCoverRegenerating &&
    !publicationCoverApproving;

  async function regeneratePublicationCover() {
    if (
      !canRegeneratePublicationCover
    ) {
      return;
    }

    try {
      setPublicationCoverRegenerating(
        true
      );

      setPublicationCoverRegenerationError(
        ""
      );

      /*
       * Keep viewing, regeneration and Creator approval visibly and
       * operationally separate. Regeneration changes only the
       * pre-approval Cover review copy.
       */
      setPublicationCoverError(
        ""
      );

      setPublicationCoverApprovalError(
        ""
      );

      const result =
        await onRegeneratePublicationCover(
          productionProjectId
        );

      const preparation =
        result?.publicationPreparation;

      if (
        !preparation ||
        typeof preparation !==
          "object" ||
        Array.isArray(
          preparation
        )
      ) {
        throw new Error(
          "Publication preparation state is unavailable."
        );
      }

      /*
       * The backend must leave the book at Creator Cover Review.
       * Adopt only its writer-safe persisted state.
       */
      setPublicationPreparation(
        preparation
      );
    } catch {
      setPublicationCoverRegenerationError(
        isFR
          ? "Impossible de régénérer la couverture finalisée pour le moment."
          : "Unable to regenerate the finished cover right now."
      );
    } finally {
      setPublicationCoverRegenerating(
        false
      );
    }
  }

  const canApprovePublicationCover =
    productionWriterState ===
      "review_cover" &&
    typeof
      onApprovePublicationCover ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    !publicationCoverOpening &&
    !publicationCoverRegenerating &&
    !publicationCoverApproving;

  async function approvePublicationCover() {
    if (
      !canApprovePublicationCover
    ) {
      return;
    }

    try {
      setPublicationCoverApproving(
        true
      );

      setPublicationCoverApprovalError(
        ""
      );

      const result =
        await onApprovePublicationCover(
          productionProjectId
        );

      const preparation =
        result?.publicationPreparation;

      if (
        !preparation ||
        typeof preparation !==
          "object" ||
        Array.isArray(
          preparation
        )
      ) {
        throw new Error(
          "Publication preparation state is unavailable."
        );
      }

      setPublicationPreparation(
        preparation
      );
    } catch {
      setPublicationCoverApprovalError(
        isFR
          ? "Impossible d’approuver la couverture finalisée pour le moment."
          : "Unable to approve the finished cover right now."
      );
    } finally {
      setPublicationCoverApproving(
        false
      );
    }
  }

  const canViewPublicationInterior =
    productionWriterState ===
      "review_interior" &&
    typeof
      onLoadPublicationInteriorReview ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    !publicationInteriorOpening &&
    !publicationInteriorRegenerating;

  async function viewPublicationInterior() {
    if (
      !canViewPublicationInterior
    ) {
      return;
    }

    try {
      setPublicationInteriorOpening(
        true
      );

      setPublicationInteriorError(
        ""
      );

      const blob =
        await onLoadPublicationInteriorReview(
          productionProjectId
        );

      if (
        !blob ||
        typeof blob !==
          "object"
      ) {
        throw new Error(
          "The finished interior PDF is unavailable."
        );
      }

      if (
        typeof URL ===
          "undefined" ||
        typeof URL.createObjectURL !==
          "function"
      ) {
        throw new Error(
          "This browser cannot open the finished interior."
        );
      }

      const objectUrl =
        URL.createObjectURL(
          blob
        );

      try {
        if (
          typeof document ===
            "undefined" ||
          typeof document
            .createElement !==
            "function"
        ) {
          throw new Error(
            "This browser cannot open the finished interior."
          );
        }

        const link =
          document.createElement(
            "a"
          );

        link.href =
          objectUrl;

        link.target =
          "_blank";

        link.rel =
          "noopener noreferrer";

        link.setAttribute(
          "aria-label",
          "View Interior"
        );

        document.body
          .appendChild(
            link
          );

        link.click();
        link.remove();
      } catch (error) {
        if (
          typeof URL
            .revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            objectUrl
          );
        }

        throw error;
      }

      /*
       * Keep the Blob URL alive long enough for the browser PDF
       * viewer to adopt it, then release it. This viewing action
       * changes no publication authority.
       */
      if (
        typeof window !==
          "undefined" &&
        typeof window
          .setTimeout ===
          "function" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        window.setTimeout(
          () => {
            URL.revokeObjectURL(
              objectUrl
            );
          },
          60000
        );
      }
    } catch (error) {
      setPublicationInteriorError(
        String(
          error?.message || ""
        ).trim() ||
        (
          isFR
            ? "Impossible d’ouvrir l’intérieur finalisé."
            : "Unable to open the finished interior."
        )
      );
    } finally {
      setPublicationInteriorOpening(
        false
      );
    }
  }

  const canViewPublicationCover =
    productionWriterState ===
      "review_cover" &&
    typeof
      onLoadPublicationCoverReview ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    !publicationCoverOpening &&
    !publicationCoverRegenerating;

  async function viewPublicationCover() {
    if (
      !canViewPublicationCover
    ) {
      return;
    }

    try {
      setPublicationCoverOpening(
        true
      );

      setPublicationCoverError(
        ""
      );

      const blob =
        await onLoadPublicationCoverReview(
          productionProjectId
        );

      if (
        !blob ||
        typeof blob !==
          "object"
      ) {
        throw new Error(
          "The finished cover PDF is unavailable."
        );
      }

      if (
        typeof URL ===
          "undefined" ||
        typeof URL.createObjectURL !==
          "function"
      ) {
        throw new Error(
          "This browser cannot open the finished cover."
        );
      }

      const objectUrl =
        URL.createObjectURL(
          blob
        );

      try {
        if (
          typeof document ===
            "undefined" ||
          typeof document
            .createElement !==
            "function"
        ) {
          throw new Error(
            "This browser cannot open the finished cover."
          );
        }

        const link =
          document.createElement(
            "a"
          );

        link.href =
          objectUrl;

        link.target =
          "_blank";

        link.rel =
          "noopener noreferrer";

        link.setAttribute(
          "aria-label",
          "View Cover"
        );

        document.body
          .appendChild(
            link
          );

        link.click();
        link.remove();
      } catch (error) {
        if (
          typeof URL
            .revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            objectUrl
          );
        }

        throw error;
      }

      /*
       * Keep the Blob URL alive long enough for the browser PDF
       * viewer to adopt it, then release it. Viewing changes no
       * Creator Cover Approval or publication authority.
       */
      if (
        typeof window !==
          "undefined" &&
        typeof window
          .setTimeout ===
          "function" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        window.setTimeout(
          () => {
            URL.revokeObjectURL(
              objectUrl
            );
          },
          60000
        );
      }
    } catch (error) {
      setPublicationCoverError(
        String(
          error?.message || ""
        ).trim() ||
        (
          isFR
            ? "Impossible d’ouvrir la couverture finalisée."
            : "Unable to open the finished cover."
        )
      );
    } finally {
      setPublicationCoverOpening(
        false
      );
    }
  }

  const canOpenFinalPublicationInterior =
    productionWriterState ===
      "ready_for_publication" &&
    publicationPreparation
      ?.publicationReady ===
      true &&
    typeof
      onLoadPublicationFinalInterior ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    !publicationFinalInteriorOpening;

  async function openFinalPublicationInterior() {
    if (
      !canOpenFinalPublicationInterior
    ) {
      return;
    }

    try {
      setPublicationFinalInteriorOpening(
        true
      );

      setPublicationFinalInteriorError(
        ""
      );

      const blob =
        await onLoadPublicationFinalInterior(
          productionProjectId
        );

      if (
        !blob ||
        typeof blob !==
          "object"
      ) {
        throw new Error(
          "The final publication interior PDF is unavailable."
        );
      }

      if (
        typeof URL ===
          "undefined" ||
        typeof URL.createObjectURL !==
          "function"
      ) {
        throw new Error(
          "This browser cannot open the final publication interior."
        );
      }

      const objectUrl =
        URL.createObjectURL(
          blob
        );

      try {
        if (
          typeof document ===
            "undefined" ||
          typeof document
            .createElement !==
            "function"
        ) {
          throw new Error(
            "This browser cannot open the final publication interior."
          );
        }

        const link =
          document.createElement(
            "a"
          );

        link.href =
          objectUrl;

        link.target =
          "_blank";

        link.rel =
          "noopener noreferrer";

        link.setAttribute(
          "aria-label",
          "View or Download Final Interior"
        );

        document.body
          .appendChild(
            link
          );

        link.click();
        link.remove();
      } catch (error) {
        if (
          typeof URL
            .revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            objectUrl
          );
        }

        throw error;
      }

      if (
        typeof window !==
          "undefined" &&
        typeof window
          .setTimeout ===
          "function" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        window.setTimeout(
          () => {
            URL.revokeObjectURL(
              objectUrl
            );
          },
          60000
        );
      }
    } catch (error) {
      setPublicationFinalInteriorError(
        String(
          error?.message || ""
        ).trim() ||
        (
          isFR
            ? "Impossible d’ouvrir l’intérieur final de publication."
            : "Unable to open the final publication interior."
        )
      );
    } finally {
      setPublicationFinalInteriorOpening(
        false
      );
    }
  }

  const canOpenFinalPublicationCover =
    productionWriterState ===
      "ready_for_publication" &&
    publicationPreparation
      ?.publicationReady ===
      true &&
    typeof
      onLoadPublicationFinalCover ===
      "function" &&
    Boolean(
      productionProjectId
    ) &&
    !publicationFinalCoverOpening;

  async function openFinalPublicationCover() {
    if (
      !canOpenFinalPublicationCover
    ) {
      return;
    }

    try {
      setPublicationFinalCoverOpening(
        true
      );

      setPublicationFinalCoverError(
        ""
      );

      const blob =
        await onLoadPublicationFinalCover(
          productionProjectId
        );

      if (
        !blob ||
        typeof blob !==
          "object"
      ) {
        throw new Error(
          "The final publication cover PDF is unavailable."
        );
      }

      if (
        typeof URL ===
          "undefined" ||
        typeof URL.createObjectURL !==
          "function"
      ) {
        throw new Error(
          "This browser cannot open the final publication cover."
        );
      }

      const objectUrl =
        URL.createObjectURL(
          blob
        );

      try {
        if (
          typeof document ===
            "undefined" ||
          typeof document
            .createElement !==
            "function"
        ) {
          throw new Error(
            "This browser cannot open the final publication cover."
          );
        }

        const link =
          document.createElement(
            "a"
          );

        link.href =
          objectUrl;

        link.target =
          "_blank";

        link.rel =
          "noopener noreferrer";

        link.setAttribute(
          "aria-label",
          "View or Download Final Cover"
        );

        document.body
          .appendChild(
            link
          );

        link.click();
        link.remove();
      } catch (error) {
        if (
          typeof URL
            .revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            objectUrl
          );
        }

        throw error;
      }

      if (
        typeof window !==
          "undefined" &&
        typeof window
          .setTimeout ===
          "function" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        window.setTimeout(
          () => {
            URL.revokeObjectURL(
              objectUrl
            );
          },
          60000
        );
      }
    } catch (error) {
      setPublicationFinalCoverError(
        String(
          error?.message || ""
        ).trim() ||
        (
          isFR
            ? "Impossible d’ouvrir la couverture finale de publication."
            : "Unable to open the final publication cover."
        )
      );
    } finally {
      setPublicationFinalCoverOpening(
        false
      );
    }
  }

  const blockingChecks =
    checks.filter(
      (check) =>
        !check.ready
    );

  const actionableWarnings =
    warnings.filter(
      (warning) =>
        !warning.ready &&
        !warning.pending
    );

  const attentionItems = [
    ...blockingChecks,
    ...actionableWarnings,
  ];

  const primaryIssue =
    attentionItems[0] ||
    null;

  const primaryAction =
    primaryIssue?.onOpen ||
    onOpenPublicationProof;

  const primaryActionLabel =
    primaryIssue
      ? (
          primaryIssue.actionLabel ||
          (
            isFR
              ? "Vérifier cet élément"
              : "Review this item"
          )
        )
      : (
          isFR
            ? "Vérifier le livre pour publication"
            : "Review book for publication"
        );

  const statusDetail =
    overallStatus === "ready"
      ? (
          isFR
            ? "Votre manuscrit, vos illustrations, votre couverture et vos choix de publication sont prêts. NextScenes va maintenant vérifier l’ajustement exact des pages, la pagination physique et les exigences d’impression."
            : "Your manuscript, illustrations, cover, and publication settings are ready. NextScenes will now check exact page fit, physical pagination, and print requirements."
        )
      : overallStatus === "warning"
        ? (
            isFR
              ? "Les éléments essentiels sont prêts. Vérifiez le détail signalé avant la révision finale."
              : "The essential parts are ready. Review the flagged detail before the final review."
          )
        : (
            isFR
              ? "NextScenes affiche uniquement ce qui nécessite encore votre attention. Corrigez ces éléments et nous vérifierons de nouveau le livre."
              : "NextScenes shows only what still needs your attention. Fix these items and we’ll check the book again."
          );

  return (
    <section
      aria-label={
        isFR
          ? "Préparer pour publication"
          : "Prepare for Publication"
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 16,
          flexWrap:
            "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.12em",
              textTransform:
                "uppercase",
              color: "#b45309",
            }}
          >
            NextScenes Book Blueprint ·{" "}
            {isFR
              ? "Préparer pour publication"
              : "Prepare for Publication"}
          </div>

          <h2
            className="ns-section-title"
            style={{
              margin:
                "5px 0 8px",
            }}
          >
            {isFR
              ? "Préparez votre livre pour publication"
              : "Prepare Your Book for Publication"}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 820,
              lineHeight: 1.6,
            }}
          >
            {isFR
              ? "NextScenes vérifie automatiquement votre manuscrit, vos illustrations, votre couverture et vos choix de publication. Vous n’avez à intervenir que lorsqu’un élément nécessite votre attention."
              : "NextScenes automatically checks your manuscript, illustrations, cover, and publication settings. You only need to act when something needs your attention."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onExit}
        >
          {isFR
            ? "Retour à Mes projets"
            : "Return to My Projects"}
        </button>
      </div>

      <div
        className="ns-card ns-nbb-major-panel"
        aria-live="polite"
        style={{
          padding:
            "clamp(20px, 3vw, 28px)",
          marginBottom: 18,
          borderRadius: 20,

          border:
            overallStatus === "ready"
              ? "1px solid rgba(22,101,52,0.24)"
              : overallStatus === "warning"
                ? "1px solid rgba(146,64,14,0.28)"
                : "1px solid rgba(185,28,28,0.24)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 16,
            flexWrap:
              "wrap",
          }}
        >
          <div
            style={{
              maxWidth: 760,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: "#64748b",
              }}
            >
              {isFR
                ? "Prochaine étape"
                : "Next step"}
            </div>

            <h3
              className="ns-card-title"
              style={{
                margin:
                  "6px 0 0",
              }}
            >
              {overallText}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin:
                  "10px 0 0",
                lineHeight: 1.6,
              }}
            >
              {statusDetail}
            </p>
          </div>

          <span
            style={statusStyle(
              overallStatus
            )}
          >
            {overallStatus === "ready"
              ? (
                  isFR
                    ? "Prêt"
                    : "Ready"
                )
              : overallStatus === "warning"
                ? (
                    isFR
                      ? "À vérifier"
                      : "Review"
                  )
                : (
                    isFR
                      ? "À corriger"
                      : "Needs attention"
                  )}
          </span>
        </div>

        {productionWriterState !==
          "ready_for_publication" ? (
        <button
          className="ns-button ns-button-primary"
          type="button"
          disabled={
            typeof primaryAction !==
            "function"
          }
          onClick={() => {
            primaryAction?.();
          }}
          style={{
            marginTop: 18,
            minWidth: 210,
          }}
        >
          {primaryActionLabel}
        </button>
        ) : null}
      </div>

      {overallStatus === "ready" &&
      printPublicationSelected ? (
        <div
          className="ns-card ns-nbb-major-panel"
          aria-label={
            isFR
              ? "Choix de publication imprimée"
              : "Print publication choices"
          }
          style={{
            padding: 18,
            marginBottom: 18,
            borderRadius: 18,
          }}
        >
          <h3
            className="ns-card-title"
            style={{
              margin: 0,
            }}
          >
            {isFR
              ? "Choix de publication"
              : "Publication choices"}
          </h3>

          {!printProductionSetupSupported ? (
            <>
              <p
                className="ns-card-meta"
                style={{
                  margin:
                    "8px 0 0",
                  lineHeight: 1.55,
                }}
              >
                {isFR
                  ? "La préparation imprimée NextScenes prend actuellement en charge les livres portrait de 5,5 × 8,5 pouces, les livres portrait de 6 × 9 pouces, les livres carrés de 8,25 × 8,25 pouces, les livres carrés de 8,5 × 8,5 pouces, les livres portrait de 8 × 10 pouces et les livres portrait de 8,5 × 11 pouces, avec un choix explicite de fond perdu."
                  : "NextScenes print production currently supports 5.5 × 8.5 inch portrait books, 6 × 9 inch portrait books, 8.25 × 8.25 inch square books, 8.5 × 8.5 inch square books, 8 × 10 inch portrait books, and 8.5 × 11 inch portrait books with an explicit bleed choice."}
              </p>

              {typeof
                onOpenProjectPlan ===
                "function" ? (
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={
                    onOpenProjectPlan
                  }
                  style={{
                    marginTop: 14,
                  }}
                >
                  {isFR
                    ? "Vérifier le plan du projet"
                    : "Review Project Plan"}
                </button>
              ) : null}
            </>
          ) : (
            <>
              <p
                className="ns-card-meta"
                style={{
                  margin:
                    "8px 0 0",
                  lineHeight: 1.55,
                  maxWidth: 760,
                }}
              >
                {isFR
                  ? "Choisissez seulement le papier et la couleur intérieure. NextScenes utilise les autres choix d’impression déjà enregistrés dans votre plan."
                  : "Choose only the paper and interior colour. NextScenes uses the other print choices already saved in your Project Plan."}
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 14,
                  marginTop: 16,
                  maxWidth: 720,
                }}
              >
                <label
                  style={{
                    display: "grid",
                    gap: 7,
                    fontWeight: 800,
                    color: "#334155",
                  }}
                >
                  <span>
                    {isFR
                      ? "Papier"
                      : "Paper"}
                    <span
                      aria-hidden="true"
                      style={{
                        color: "#b91c1c",
                        marginLeft: 3,
                      }}
                    >
                      *
                    </span>
                  </span>

                  <select
                    className="ns-input"
                    aria-label={
                      isFR
                        ? "Papier"
                        : "Paper"
                    }
                    value={paperType}
                    disabled={
                      publicationAlreadyStarted ||
                      publicationPreparationLoading ||
                      publicationPreparationStarting
                    }
                    onChange={(event) =>
                      setPaperType(
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      {isFR
                        ? "Choisir le papier"
                        : "Choose paper"}
                    </option>
                    <option value="white">
                      {isFR
                        ? "Blanc"
                        : "White"}
                    </option>
                    <option value="cream">
                      {isFR
                        ? "Crème"
                        : "Cream"}
                    </option>
                  </select>
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 7,
                    fontWeight: 800,
                    color: "#334155",
                  }}
                >
                  <span>
                    {isFR
                      ? "Couleur intérieure"
                      : "Interior colour"}
                    <span
                      aria-hidden="true"
                      style={{
                        color: "#b91c1c",
                        marginLeft: 3,
                      }}
                    >
                      *
                    </span>
                  </span>

                  <select
                    className="ns-input"
                    aria-label={
                      isFR
                        ? "Couleur intérieure"
                        : "Interior colour"
                    }
                    value={
                      interiorColor
                    }
                    disabled={
                      publicationAlreadyStarted ||
                      publicationPreparationLoading ||
                      publicationPreparationStarting
                    }
                    onChange={(event) =>
                      setInteriorColor(
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      {isFR
                        ? "Choisir la couleur intérieure"
                        : "Choose interior colour"}
                    </option>
                    <option value="black_and_white">
                      {isFR
                        ? "Noir et blanc"
                        : "Black & white"}
                    </option>
                    <option value="standard_color">
                      {isFR
                        ? "Couleur standard"
                        : "Standard colour"}
                    </option>
                    <option value="premium_color">
                      {isFR
                        ? "Couleur premium"
                        : "Premium colour"}
                    </option>
                  </select>
                </label>
              </div>

              {colourPaperConflict ? (
                <p
                  role="alert"
                  className="ns-card-meta"
                  style={{
                    margin:
                      "12px 0 0",
                    color: "#92400e",
                    fontWeight: 700,
                  }}
                >
                  {isFR
                    ? "Les intérieurs en couleur nécessitent du papier blanc."
                    : "Colour interiors require white paper."}
                </p>
              ) : null}

              {publicationCompatibilityPreviewLoading ? (
                <p
                  className="ns-card-meta"
                  style={{
                    margin:
                      "12px 0 0",
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "Vérification des exigences de cette option d’impression…"
                    : "Checking the requirements for this print option…"}
                </p>
              ) : null}

              {publicationCompatibilityPreviewError ? (
                <p
                  role="alert"
                  className="ns-card-meta"
                  style={{
                    margin:
                      "12px 0 0",
                    color:
                      "#92400e",
                    fontWeight: 700,
                    lineHeight: 1.5,
                  }}
                >
                  {
                    publicationCompatibilityPreviewError
                  }
                </p>
              ) : null}

              {compatibilityPreviewRangeKnown ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: 14,
                    borderRadius: 10,
                    background:
                      plannedStructureBelowMinimum
                        ? "#fef3c7"
                        : "#f8fafc",
                    color:
                      plannedStructureBelowMinimum
                        ? "#78350f"
                        : "#334155",
                    maxWidth: 760,
                    lineHeight: 1.55,
                  }}
                >
                  <strong>
                    {isFR
                      ? "Exigence d’impression"
                      : "Print requirement"}
                  </strong>

                  <p
                    className="ns-card-meta"
                    style={{
                      margin:
                        "7px 0 0",
                      color:
                        "inherit",
                      lineHeight:
                        1.55,
                    }}
                  >
                    {isFR
                      ? `Pour les réglages d’impression ${productionTrimDisplay}, l’option sélectionnée accepte de ${compatibilityPreviewMinimum} à ${compatibilityPreviewMaximum} pages finalisées.`
                      : `For the selected ${productionTrimDisplay} print settings, this option supports ${compatibilityPreviewMinimum}–${compatibilityPreviewMaximum} finished pages.`}
                  </p>

                  {plannedStructureBelowMinimum ? (
                    <p
                      className="ns-card-meta"
                      style={{
                        margin:
                          "7px 0 0",
                        color:
                          "inherit",
                        lineHeight:
                          1.55,
                      }}
                    >
                      {isFR
                        ? `Votre Structure du livre contient actuellement environ ${plannedBookStructurePageCount} pages physiques planifiées. C’est inférieur au minimum de ${compatibilityPreviewMinimum} pages. Vous pouvez continuer à créer votre livre, mais cette option d’impression exigera au moins ${compatibilityPreviewMinimum} pages finalisées avant que la production imprimée puisse continuer. Il s’agit d’un avertissement de planification, pas encore du nombre final de pages.`
                        : `Your current Book Structure contains about ${plannedBookStructurePageCount} planned physical pages, which is below the ${compatibilityPreviewMinimum}-page minimum. You can continue creating your book, but this print option will require at least ${compatibilityPreviewMinimum} finished pages before print production can continue. This is an early planning warning, not the final page count.`}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {publicationPreparationLoading ? (
                <p
                  className="ns-card-meta"
                  role="status"
                  style={{
                    margin:
                      "12px 0 0",
                  }}
                >
                  {isFR
                    ? "Vérification de la préparation existante…"
                    : "Checking existing publication preparation…"}
                </p>
              ) : null}

              {publicationPreparationError ? (
                <p
                  className="ns-card-meta"
                  role="alert"
                  style={{
                    margin:
                      "12px 0 0",
                    color: "#b91c1c",
                    fontWeight: 700,
                  }}
                >
                  {
                    publicationPreparationError
                  }
                </p>
              ) : null}

              {publicationAlreadyStarted ? (
                <div
                  role="status"
                  style={{
                    marginTop: 16,
                    padding: 14,
                    borderRadius: 14,
                    background: "#f0fdf4",
                    border:
                      "1px solid rgba(22,101,52,0.18)",
                  }}
                >
                  <strong>
                    {
                      productionWriterStateText
                    }
                  </strong>

                  <p
                    className="ns-card-meta"
                    style={{
                      margin:
                        "6px 0 0",
                    }}
                  >
                    {isFR
                      ? "Vos choix d’impression sont liés à cette préparation."
                      : "Your print choices are linked to this preparation."}
                  </p>

                  {productionWriterState ===
                    "review_interior" ? (
                    <>
                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        disabled={
                          !canViewPublicationInterior
                        }
                        onClick={
                          viewPublicationInterior
                        }
                        style={{
                          marginTop: 14,
                          minWidth: 180,
                        }}
                      >
                        {publicationInteriorOpening
                          ? (
                              isFR
                                ? "Ouverture…"
                                : "Opening…"
                            )
                          : (
                              isFR
                                ? "Voir l’intérieur"
                                : "View Interior"
                            )}
                      </button>

                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "9px 0 0",
                          lineHeight: 1.5,
                        }}
                      >
                        {isFR
                          ? "L’intérieur s’ouvre dans le lecteur PDF de votre navigateur dans un nouvel onglet. Pour un livre long, utilisez le numéro de page ou les miniatures du lecteur PDF pour accéder directement à la page souhaitée. Cette consultation sert uniquement à la révision. Elle n’approuve pas l’intérieur, ne verrouille pas le nombre de pages, ne publie pas le livre et ne le téléverse pas."
                          : "The Interior opens in your browser’s PDF viewer in a new tab. For a long book, use the PDF viewer’s page number or page thumbnails to jump directly to the page you want. Viewing is for review only. It does not approve the interior, lock the page count, publish the book, or upload it."}
                      </p>

                      {typeof
                        onRegeneratePublicationInterior ===
                        "function" ? (
                        <>
                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            disabled={
                              !canRegeneratePublicationInterior
                            }
                            onClick={
                              regeneratePublicationInterior
                            }
                            style={{
                              marginTop: 14,
                              minWidth: 180,
                            }}
                          >
                            {publicationInteriorRegenerating
                              ? (
                                  isFR
                                    ? "Régénération…"
                                    : "Regenerating…"
                                )
                              : (
                                  isFR
                                    ? "Régénérer l’intérieur"
                                    : "Regenerate Interior"
                                )}
                          </button>

                          <p
                            className="ns-card-meta"
                            style={{
                              margin:
                                "9px 0 0",
                              lineHeight: 1.5,
                            }}
                          >
                            {isFR
                              ? "Régénérez uniquement la copie de révision de l’intérieur. Cette action n’approuve pas l’intérieur et ne verrouille pas le nombre final de pages."
                              : "Regenerate only the Interior review copy. This does not approve the interior or lock the final page count."}
                          </p>

                          {publicationInteriorRegenerationError ? (
                            <p
                              role="alert"
                              className="ns-card-meta"
                              style={{
                                margin:
                                  "9px 0 0",
                                color:
                                  "#b91c1c",
                                fontWeight: 700,
                              }}
                            >
                              {
                                publicationInteriorRegenerationError
                              }
                            </p>
                          ) : null}
                        </>
                      ) : null}

                      {publicationInteriorCompatibilityMessage ? (
                        <div
                          role="alert"
                          style={{
                            marginTop: 14,
                            padding: 14,
                            borderRadius: 10,
                            background:
                              "#fef3c7",
                            color:
                              "#78350f",
                            lineHeight: 1.55,
                            maxWidth: 760,
                          }}
                        >
                          <strong>
                            {isFR
                              ? "Compatibilité d’impression à vérifier"
                              : "Print compatibility needs attention"}
                          </strong>

                          <p
                            className="ns-card-meta"
                            style={{
                              margin:
                                "7px 0 0",
                              color:
                                "inherit",
                              lineHeight:
                                1.55,
                            }}
                          >
                            {
                              publicationInteriorCompatibilityMessage
                            }
                          </p>

                          {(
                            publicationPageCountBelowMinimum ||
                            publicationPageCountAboveMaximum
                          ) &&
                          typeof onOpenBookMap ===
                            "function" ? (
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              onClick={
                                onOpenBookMap
                              }
                              style={{
                                marginTop:
                                  10,
                              }}
                            >
                              {isFR
                                ? "Vérifier la structure du livre"
                                : "Review Book Structure"}
                            </button>
                          ) : null}
                        </div>
                      ) : null}

                      {typeof
                        onApprovePublicationInterior ===
                        "function" ? (
                        <>
                          <button
                            className="ns-button ns-button-primary"
                            type="button"
                            disabled={
                              !canApprovePublicationInterior
                            }
                            onClick={
                              approvePublicationInterior
                            }
                            style={{
                              marginTop: 14,
                              minWidth: 180,
                            }}
                          >
                            {publicationInteriorApproving
                              ? (
                                  isFR
                                    ? "Approbation…"
                                    : "Approving…"
                                )
                              : (
                                  isFR
                                    ? "Approuver l’intérieur"
                                    : "Approve Interior"
                                )}
                          </button>

                          <p
                            className="ns-card-meta"
                            style={{
                              margin:
                                "9px 0 0",
                              lineHeight: 1.5,
                            }}
                          >
                            {isFR
                              ? "Approuvez uniquement après avoir vérifié l’intérieur finalisé. Cette action verrouille le nombre final de pages et permet de préparer la couverture. Elle ne publie pas et ne téléverse pas le livre."
                              : "Approve only after reviewing the finished interior. This locks the final page count and allows cover preparation. It does not publish or upload the book."}
                          </p>

                          {publicationInteriorApprovalError ? (
                            <p
                              role="alert"
                              className="ns-card-meta"
                              style={{
                                margin:
                                  "9px 0 0",
                                color:
                                  "#b91c1c",
                                fontWeight: 700,
                              }}
                            >
                              {
                                publicationInteriorApprovalError
                              }
                            </p>
                          ) : null}
                        </>
                      ) : null}

                      {publicationInteriorError ? (
                        <p
                          role="alert"
                          className="ns-card-meta"
                          style={{
                            margin:
                              "9px 0 0",
                            color:
                              "#b91c1c",
                            fontWeight: 700,
                          }}
                        >
                          {
                            publicationInteriorError
                          }
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  {productionWriterState ===
                    "review_cover" ? (
                    <>
                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        disabled={
                          !canViewPublicationCover
                        }
                        onClick={
                          viewPublicationCover
                        }
                        style={{
                          marginTop: 14,
                          minWidth: 180,
                        }}
                      >
                        {publicationCoverOpening
                          ? (
                              isFR
                                ? "Ouverture…"
                                : "Opening…"
                            )
                          : (
                              isFR
                                ? "Voir la couverture"
                                : "View Cover"
                            )}
                      </button>

                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "9px 0 0",
                          lineHeight: 1.5,
                        }}
                      >
                        {isFR
                          ? "Cette consultation sert uniquement à la révision. Elle n’approuve pas la couverture, ne conserve pas les artefacts de publication, ne publie pas le livre et ne le téléverse pas."
                          : "Viewing is for review only. It does not approve the cover, persist publication artifacts, publish the book, or upload it."}
                      </p>

                      {typeof
                        onRegeneratePublicationCover ===
                        "function" ? (
                        <>
                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            disabled={
                              !canRegeneratePublicationCover
                            }
                            onClick={
                              regeneratePublicationCover
                            }
                            style={{
                              marginTop: 14,
                              minWidth: 180,
                            }}
                          >
                            {publicationCoverRegenerating
                              ? (
                                  isFR
                                    ? "Régénération…"
                                    : "Regenerating…"
                                )
                              : (
                                  isFR
                                    ? "Régénérer la couverture"
                                    : "Regenerate Cover"
                                )}
                          </button>

                          <p
                            className="ns-card-meta"
                            style={{
                              margin:
                                "9px 0 0",
                              lineHeight: 1.5,
                            }}
                          >
                            {isFR
                              ? "Régénérez uniquement la copie de révision de la couverture. Cette action n’approuve pas la couverture, ne conserve pas les artefacts de publication, ne publie pas le livre et ne le téléverse pas."
                              : "Regenerate only the Cover review copy. This does not approve the cover, persist publication artifacts, publish the book, or upload it."}
                          </p>

                          {publicationCoverRegenerationError ? (
                            <p
                              role="alert"
                              className="ns-card-meta"
                              style={{
                                margin:
                                  "9px 0 0",
                                color:
                                  "#b91c1c",
                                fontWeight: 700,
                              }}
                            >
                              {
                                publicationCoverRegenerationError
                              }
                            </p>
                          ) : null}
                        </>
                      ) : null}

                      {typeof
                        onApprovePublicationCover ===
                        "function" ? (
                        <>
                          <button
                            className="ns-button ns-button-primary"
                            type="button"
                            disabled={
                              !canApprovePublicationCover
                            }
                            onClick={
                              approvePublicationCover
                            }
                            style={{
                              marginTop: 14,
                              minWidth: 180,
                            }}
                          >
                            {publicationCoverApproving
                              ? (
                                  isFR
                                    ? "Approbation…"
                                    : "Approving…"
                                )
                              : (
                                  isFR
                                    ? "Approuver la couverture"
                                    : "Approve Cover"
                                )}
                          </button>

                          <p
                            className="ns-card-meta"
                            style={{
                              margin:
                                "9px 0 0",
                              lineHeight: 1.5,
                            }}
                          >
                            {isFR
                              ? "Approuvez uniquement après avoir vérifié la couverture finalisée. Cette action autorise l’étape technique suivante pour enregistrer les fichiers de publication approuvés. L’approbation elle-même n’enregistre pas ces fichiers, ne publie pas le livre et ne le téléverse pas."
                              : "Approve only after reviewing the finished cover. This authorizes the next technical step to save the approved publication files. The approval itself does not save those files, publish the book, or upload it."}
                          </p>

                          {publicationCoverApprovalError ? (
                            <p
                              role="alert"
                              className="ns-card-meta"
                              style={{
                                margin:
                                  "9px 0 0",
                                color:
                                  "#b91c1c",
                                fontWeight: 700,
                              }}
                            >
                              {
                                publicationCoverApprovalError
                              }
                            </p>
                          ) : null}
                        </>
                      ) : null}

                      {publicationCoverError ? (
                        <p
                          role="alert"
                          className="ns-card-meta"
                          style={{
                            margin:
                              "9px 0 0",
                            color:
                              "#b91c1c",
                            fontWeight: 700,
                          }}
                        >
                          {
                            publicationCoverError
                          }
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  {productionWriterState ===
                    "ready_for_publication" ? (
                    <>
                      <p
                        className="ns-card-meta"
                      >
                        {isFR
                          ? "Le papier et la couleur intérieure sont verrouillés pour ces fichiers de publication finalisés."
                          : "Paper and interior colour are locked to these finished publication files."}
                      </p>
                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "12px 0 0",
                          lineHeight: 1.5,
                        }}
                      >
                        {isFR
                          ? "Vos fichiers finaux de publication sont prêts. Vous pouvez les ouvrir pour les vérifier ou les télécharger. Cette action ne publie pas le livre et ne le téléverse pas."
                          : "Your final publication files are ready. You can open them to review or download them. This does not publish or upload the book."}
                      </p>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          flexWrap: "wrap",
                          marginTop: 14,
                        }}
                      >
                        <button
                          className="ns-button ns-button-primary"
                          type="button"
                          disabled={
                            !canOpenFinalPublicationInterior
                          }
                          onClick={
                            openFinalPublicationInterior
                          }
                        >
                          {publicationFinalInteriorOpening
                            ? (
                                isFR
                                  ? "Ouverture…"
                                  : "Opening…"
                              )
                            : (
                                isFR
                                  ? "Voir ou télécharger l’intérieur final"
                                  : "View or Download Final Interior"
                              )}
                        </button>

                        <button
                          className="ns-button ns-button-primary"
                          type="button"
                          disabled={
                            !canOpenFinalPublicationCover
                          }
                          onClick={
                            openFinalPublicationCover
                          }
                        >
                          {publicationFinalCoverOpening
                            ? (
                                isFR
                                  ? "Ouverture…"
                                  : "Opening…"
                              )
                            : (
                                isFR
                                  ? "Voir ou télécharger la couverture finale"
                                  : "View or Download Final Cover"
                              )}
                        </button>
                      </div>

                      {publicationFinalInteriorError ? (
                        <p
                          role="alert"
                          className="ns-card-meta"
                          style={{
                            margin:
                              "9px 0 0",
                            color:
                              "#b91c1c",
                            fontWeight: 700,
                          }}
                        >
                          {
                            publicationFinalInteriorError
                          }
                        </p>
                      ) : null}

                      {publicationFinalCoverError ? (
                        <p
                          role="alert"
                          className="ns-card-meta"
                          style={{
                            margin:
                              "9px 0 0",
                            color:
                              "#b91c1c",
                            fontWeight: 700,
                          }}
                        >
                          {
                            publicationFinalCoverError
                          }
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  {productionWriterState !==
                    "review_interior" &&
                  productionWriterState !==
                    "review_cover" &&
                  productionWriterState !==
                    "ready_for_publication" ? (
                    <button
                      className="ns-button ns-button-primary"
                      type="button"
                      disabled={
                        !canRetryPublicationPreparation
                      }
                      onClick={
                        startPublicationPreparation
                      }
                      style={{
                        marginTop: 14,
                        minWidth: 230,
                      }}
                    >
                      {publicationPreparationStarting
                        ? (
                            isFR
                              ? "Nouvelle tentative…"
                              : "Retrying…"
                          )
                        : (
                            isFR
                              ? "Réessayer la préparation"
                              : "Retry publication preparation"
                          )}
                    </button>
                  ) : null}
                </div>
              ) : (
                <>
                  <button
                    className="ns-button ns-button-primary"
                    type="button"
                    disabled={
                      !canStartPublicationPreparation
                    }
                    onClick={
                      startPublicationPreparation
                    }
                    style={{
                      marginTop: 16,
                      minWidth: 230,
                    }}
                  >
                    {publicationPreparationStarting
                      ? (
                          isFR
                            ? "Démarrage…"
                            : "Starting…"
                        )
                      : (
                          isFR
                            ? "Démarrer la préparation"
                            : "Start publication preparation"
                        )}
                  </button>

                  {!publicationStartAvailable ? (
                    <p
                      className="ns-card-meta"
                      style={{
                        margin:
                          "10px 0 0",
                        lineHeight: 1.5,
                      }}
                    >
                      {isFR
                        ? "La préparation est actuellement en lecture seule. Ces choix ne sont pas envoyés ni enregistrés."
                        : "Publication preparation is currently read-only. These choices are not sent or saved."}
                    </p>
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
      ) : null}

      {attentionItems.length > 0 ? (
        <div
          className="ns-card ns-nbb-major-panel"
          style={{
            padding: 18,
            marginBottom: 18,
            borderRadius: 18,
          }}
        >
          <h3
            className="ns-card-title"
            style={{
              margin: 0,
            }}
          >
            {isFR
              ? "À vérifier"
              : "Needs attention"}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "7px 0 0",
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Seuls les éléments qui nécessitent votre intervention sont affichés ici."
              : "Only the items that still need your attention are shown here."}
          </p>

          <div
            style={{
              display: "grid",
              gap: 12,
              marginTop: 14,
            }}
          >
            {attentionItems.map(
              (issue) => (
                <article
                  key={issue.id}
                  className="ns-nbb-inner-panel"
                  style={{
                    padding: 15,
                    borderRadius: 14,
                    background: "#fff",
                    border:
                      "1px solid rgba(185,28,28,0.18)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems:
                        "flex-start",
                      gap: 12,
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: 720,
                      }}
                    >
                      <h3
                        className="ns-card-title"
                        style={{
                          margin: 0,
                        }}
                      >
                        {issue.title}
                      </h3>

                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "6px 0 0",
                          lineHeight: 1.55,
                        }}
                      >
                        {issue.detail}
                      </p>
                    </div>

                    <span
                      style={statusStyle(
                        issue.pending
                          ? "warning"
                          : "blocked"
                      )}
                    >
                      {issue.pending
                        ? (
                            isFR
                              ? "À vérifier"
                              : "Review"
                          )
                        : (
                            isFR
                              ? "À corriger"
                              : "Needs attention"
                          )}
                    </span>
                  </div>

                  {typeof
                    issue.onOpen ===
                  "function" ? (
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={
                        issue.onOpen
                      }
                      style={{
                        marginTop: 12,
                      }}
                    >
                      {issue.actionLabel ||
                        (
                          isFR
                            ? "Vérifier"
                            : "Review"
                        )}
                    </button>
                  ) : null}
                </article>
              )
            )}
          </div>
        </div>
      ) : null}

      <div
        className="ns-card"
        style={{
          padding: 16,
          marginTop: 18,
          borderRadius: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            gap: 12,
            flexWrap:
              "wrap",
          }}
        >
          <div>
            <strong>
              {isFR
                ? "Détails de la préparation"
                : "Preparation details"}
            </strong>

            <p
              className="ns-card-meta"
              style={{
                margin:
                  "5px 0 0",
              }}
            >
              {isFR
                ? `${readyCount} domaine(s) prêt(s) sur ${checks.length}.`
                : `${readyCount} of ${checks.length} areas ready.`}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            aria-expanded={
              detailsOpen
            }
            onClick={() =>
              setDetailsOpen(
                (current) =>
                  !current
              )
            }
          >
            {detailsOpen
              ? (
                  isFR
                    ? "Masquer les détails"
                    : "Hide details"
                )
              : (
                  isFR
                    ? "Voir les détails"
                    : "View details"
                )}
          </button>
        </div>

        {detailsOpen ? (
          <div
            style={{
              display: "grid",
              gap: 12,
              marginTop: 16,
            }}
          >
            {checks.map(
              (check) => (
                <article
                  key={check.id}
                  className="ns-card"
                  style={{
                    padding: 15,
                    borderRadius: 14,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      gap: 12,
                      alignItems:
                        "flex-start",
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <div>
                      <h3
                        className="ns-card-title"
                        style={{
                          margin: 0,
                        }}
                      >
                        {check.title}
                      </h3>

                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "6px 0 0",
                        }}
                      >
                        {check.detail}
                      </p>
                    </div>

                    <span
                      style={statusStyle(
                        check.ready
                          ? "ready"
                          : "blocked"
                      )}
                    >
                      {check.ready
                        ? (
                            isFR
                              ? "Prêt"
                              : "Ready"
                          )
                        : (
                            isFR
                              ? "À corriger"
                              : "Needs attention"
                          )}
                    </span>
                  </div>
                </article>
              )
            )}

            <div
              style={{
                marginTop: 6,
              }}
            >
              <h3
                className="ns-card-title"
                style={{
                  margin:
                    "0 0 10px",
                }}
              >
                {isFR
                  ? "Cohérence"
                  : "Consistency"}
              </h3>

              {warnings.map(
                (warning) => (
                  <div
                    key={warning.id}
                    className="ns-nbb-inner-panel ns-nbb-consistency-item"
                    style={{
                      padding: 13,
                      marginTop: 10,
                      borderRadius: 12,
                      background:
                        "#f8fafc",
                    }}
                  >
                    <strong>
                      {warning.title}
                    </strong>

                    <p
                      className="ns-card-meta"
                      style={{
                        margin:
                          "5px 0 0",
                      }}
                    >
                      {warning.detail}
                    </p>

                    <div style={{ marginTop: 8 }}>
                      <span
                        style={
                          warning.ready
                            ? statusStyle("ready")
                            : warning.pending
                              ? {
                                  ...statusStyle("warning"),
                                  background: "#e2e8f0",
                                  color: "#475569",
                                }
                              : statusStyle("warning")
                        }
                      >
                        {warning.ready
                          ? (
                              isFR
                                ? "Cohérent"
                                : "Consistent"
                            )
                          : warning.pending
                            ? (
                                isFR
                                  ? "Pas encore vérifiable"
                                  : "Not yet checkable"
                              )
                            : (
                                isFR
                                  ? "À vérifier"
                                  : "Review"
                              )}
                      </span>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        ) : null}
      </div>

      <p
        className="ns-card-meta"
        style={{
          margin:
            "16px 0 0",
          lineHeight: 1.55,
        }}
      >
        <strong>
          {isFR
            ? "Rien n’est publié depuis cet écran."
            : "This screen publishes nothing."}
        </strong>{" "}
        {isFR
          ? "Aucun fichier n’est envoyé vers une plateforme extérieure sans une action ultérieure de votre part."
          : "No file is sent to an external platform without a later action from you."}
      </p>

      <div
        style={{
          marginTop: 18,
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onReturn}
        >
          {isFR
            ? "Retour au NextScenes Book Blueprint"
            : "Return to NextScenes Book Blueprint"}
        </button>
      </div>
    </section>
  );
}
